import { z } from 'zod';

import { MAX_IMPORT_BYTES, MAX_UPLOAD_BYTES } from './constants.js';

/** Logical kinds of upload that exist in Phases 0–5. Later phases extend
 *  this list (inspection_photo, etc.). */
export const UPLOAD_KINDS = [
  'org_logo',
  'profile_avatar',
  'building_import_csv',
  'building_import_xlsx',
  'work_order_photo',
  'work_order_document',
  'work_order_receipt',
  'work_order_signature',
  'work_order_voice_memo',
  'reminder_voice_memo',
  'compliance_log_photo',
  'compliance_log_document',
  'compliance_log_signature',
  'device_document',
  'compliance_ingest',
] as const;
export type UploadKind = (typeof UPLOAD_KINDS)[number];

export const ALLOWED_IMAGE_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;
export type AllowedImageContentType = (typeof ALLOWED_IMAGE_CONTENT_TYPES)[number];

/** Content types accepted for the CSV/XLSX import flow. Excel files get
 *  converted to CSV server-side in the worker (SheetJS). */
export const ALLOWED_IMPORT_CONTENT_TYPES = [
  'text/csv',
  'application/vnd.ms-excel', // many browsers report .csv as this on Windows
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;
export type AllowedImportContentType = (typeof ALLOWED_IMPORT_CONTENT_TYPES)[number];

const ALLOWED_DOCUMENT_CONTENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
] as const;

/** Map a (kind → content-type set) so the API can validate one against the
 *  other in a single place. */
export const UPLOAD_KIND_CONTENT_TYPES: Record<UploadKind, readonly string[]> = {
  org_logo: ALLOWED_IMAGE_CONTENT_TYPES,
  profile_avatar: ALLOWED_IMAGE_CONTENT_TYPES,
  building_import_csv: ['text/csv', 'application/vnd.ms-excel'],
  building_import_xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  work_order_photo: ALLOWED_IMAGE_CONTENT_TYPES,
  work_order_document: ALLOWED_DOCUMENT_CONTENT_TYPES,
  work_order_receipt: ALLOWED_DOCUMENT_CONTENT_TYPES,
  work_order_signature: ALLOWED_IMAGE_CONTENT_TYPES,
  work_order_voice_memo: ['audio/mpeg', 'audio/mp4', 'audio/webm', 'audio/ogg'],
  reminder_voice_memo: ['audio/mpeg', 'audio/mp4', 'audio/webm', 'audio/ogg'],
  compliance_log_photo: ALLOWED_IMAGE_CONTENT_TYPES,
  compliance_log_document: ALLOWED_DOCUMENT_CONTENT_TYPES,
  compliance_log_signature: ALLOWED_IMAGE_CONTENT_TYPES,
  device_document: ALLOWED_DOCUMENT_CONTENT_TYPES,
  compliance_ingest: ALLOWED_DOCUMENT_CONTENT_TYPES,
};

const WORK_ORDER_ATTACHMENT_MAX_BYTES = 50 * 1024 * 1024;

/** Size cap per kind. Images are tiny; imports + WO attachments larger. */
export const UPLOAD_KIND_MAX_BYTES: Record<UploadKind, number> = {
  org_logo: MAX_UPLOAD_BYTES,
  profile_avatar: MAX_UPLOAD_BYTES,
  building_import_csv: MAX_IMPORT_BYTES,
  building_import_xlsx: MAX_IMPORT_BYTES,
  work_order_photo: WORK_ORDER_ATTACHMENT_MAX_BYTES,
  work_order_document: WORK_ORDER_ATTACHMENT_MAX_BYTES,
  work_order_receipt: WORK_ORDER_ATTACHMENT_MAX_BYTES,
  work_order_signature: MAX_UPLOAD_BYTES,
  work_order_voice_memo: WORK_ORDER_ATTACHMENT_MAX_BYTES,
  reminder_voice_memo: WORK_ORDER_ATTACHMENT_MAX_BYTES,
  compliance_log_photo: WORK_ORDER_ATTACHMENT_MAX_BYTES,
  compliance_log_document: WORK_ORDER_ATTACHMENT_MAX_BYTES,
  compliance_log_signature: MAX_UPLOAD_BYTES,
  device_document: WORK_ORDER_ATTACHMENT_MAX_BYTES,
  compliance_ingest: WORK_ORDER_ATTACHMENT_MAX_BYTES,
};

/**
 * Normalizes a browser-supplied content type to its bare media type.
 *
 * `MediaRecorder` (and some file pickers) hand back values with codec/charset
 * parameters, e.g. `audio/webm;codecs=opus` or `text/csv; charset=utf-8`.
 * Those never match our exact allow-lists, so we strip everything after the
 * first `;`, trim, and lowercase before validating/storing.
 */
export function normalizeUploadContentType(value: string): string {
  return value.split(';')[0]!.trim().toLowerCase();
}

export const presignUploadInput = z
  .object({
    kind: z.enum(UPLOAD_KINDS),
    contentType: z.string().min(1).max(200).transform(normalizeUploadContentType),
    filename: z
      .string()
      .min(1)
      .max(200)
      .regex(/^[\w.\- ()]+$/, 'Filename contains unsupported characters'),
    sizeBytes: z.number().int().positive(),
  })
  .superRefine((value, ctx) => {
    const allowedTypes = UPLOAD_KIND_CONTENT_TYPES[value.kind];
    if (!allowedTypes.includes(value.contentType)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contentType'],
        message: `Content-type "${value.contentType}" is not allowed for kind "${value.kind}"`,
      });
    }
    const maxBytes = UPLOAD_KIND_MAX_BYTES[value.kind];
    if (value.sizeBytes > maxBytes) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sizeBytes'],
        message: `File is too large (max ${(maxBytes / (1024 * 1024)).toFixed(0)} MB)`,
      });
    }
  });
export type PresignUploadInput = z.infer<typeof presignUploadInput>;

export const presignUploadResult = z.object({
  uploadId: z.string().uuid(),
  uploadUrl: z.string().url(),
  objectKey: z.string(),
  headers: z.record(z.string()),
  expiresAt: z.string().datetime(),
});
export type PresignUploadResult = z.infer<typeof presignUploadResult>;

export const signedDownloadResult = z.object({
  url: z.string().url(),
  expiresAt: z.string().datetime(),
});
export type SignedDownloadResult = z.infer<typeof signedDownloadResult>;
